<template>
    <Tooltip :is-disabled="!slots['tooltip-text']">
        <template #trigger="tooltipProps">
            <span
                v-bind="tooltipProps"
                class="rounded-md ring-1 ring-inset leading-none min-w-[3.75rem] tabular-nums flex flex-row items-center justify-center gap-1.5 focus-visible:outline-2 focus-visible:outline-offset-1"
                :class="[colorClasses, sizeClasses]"
                :tabindex="0"
            >
                <template v-if="props.visibleItems > 0">
                    <div>
                        <span class="font-semibold text-sky-800">{{ props.visibleItems }}</span>
                        <span class="font-light">/{{ props.totalItems }}</span>
                    </div>
                    <VisibilityIcon
                        class="text-sky-800"
                        :class="{
                            'size-4': props.size === 'Extra-Small',
                            'size-4.5': props.size === 'Small',
                            'size-5': props.size === 'Base',
                        }"
                    />
                </template>
                <template v-else>
                    <VisibilityOffIcon
                        :class="{
                            'size-4': props.size === 'Extra-Small',
                            'size-4.5': props.size === 'Small',
                            'size-5': props.size === 'Base',
                        }"
                    />
                </template>
            </span>
        </template>

        <template #text>
            <div class="w-max">
                <slot name="tooltip-text" />
            </div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import VisibilityIcon from '../Icon/VisibilityIcon.vue';
import VisibilityOffIcon from '../Icon/VisibilityOffIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type VisibilityBadgeProps = {
    visibleItems: number;
    totalItems: number;
    size: 'Extra-Small' | 'Small' | 'Base'; // Extra-Small = 24px height, Small = 32px height, Base = 40px height
};

const props = defineProps<VisibilityBadgeProps>();

const colorClasses = computed(() => {
    if (props.visibleItems === 0) {
        return 'bg-gray-50 ring-gray-600/10 text-gray-500 focus-visible:outline-gray-700';
    }

    return 'bg-sky-50 ring-sky-600/10 text-sky-700 focus-visible:outline-sky-700';
});

const sizeClasses = computed(() => {
    switch (props.size) {
        case 'Extra-Small':
            return 'text-xs px-2 py-1';

        case 'Small':
            return 'text-base px-2.5 py-[0.4375rem]';

        case 'Base':
            return 'text-base px-3 py-[0.625rem]';

        default:
            throw new Error(`Unknown VisibilityBadge size: ${props.size}`);
    }
});

const slots = useSlots();
</script>
